import React, { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, TextField } from '@mui/material';
import { Refresh, Search } from "@mui/icons-material";
import Button from "@mui/material/Button";
import { toast } from 'react-toastify';
import { InvalidMessage } from "../../Utility/Constants";
import Table from '../../Table/Table';
import { ProductDetailsColumns } from '../../Table/TableColumn';
import { tableColumns } from '../../Redux/TableSlice';
import { currentData, clearData, updateSearch, searchClicked, updateInputError } from '../../Redux/MNR/ProductDetailSlice';
import { clearAll, searchTriggered } from '../../Redux/CNS/CommonSlice';
import {
    validateRegexNumberWithStar, validateRegexNumbersSpecialCharLong, validateRegexOnlyNumbers
} from '../../Validation/Validation';
import { GeneralError } from '../../Utility/Constants';
import Spinner from '../../Spinner/Spinner';
import { showAll, isMNR } from '../../Redux/MNR/MNRCommonSlice';

function ProductDetail() {
    const dispatch = useDispatch();
    const error = useSelector(state => state.productDetail.error);
    const isSearched = useSelector(state => state.productDetail.isSearchClicked);
    const searchTriggeredValue = useSelector(state => state.cnsCommon.searchTriggered);
    const inputError = useSelector(state => state.productDetail.inputError);
    const hasAnyError = Object.values(inputError).some(value => value === true);
    const data = useSelector(state => state.productDetail.data);
    const isSearchEnable = (
        (data.AuthProductCode && data.AuthProductCode.trim() !== '') ||
        (data.GPI14 && data.GPI14.trim() !== '') ||
        (data.NDC && data.NDC.trim() !== '')
    );
    const loading = useSelector(state => state.productDetail.loading);
    const showAllValue = useSelector(state => state.mnrCommon.showAll);

    useEffect(() => {
        if (error) {
            toast.error(GeneralError);
            return;
        }
    }, [error]);

    const handleResetInitial = (useCallback(() => {
        dispatch(clearAll());
        dispatch(clearData());
        dispatch(updateSearch(true));
        dispatch(searchClicked(false));
        dispatch(showAll(false));
        dispatch(isMNR(true));
    }, [dispatch]))

    const handleReset = () => {
        dispatch(clearData());
        dispatch(updateSearch(true));
        dispatch(searchClicked(false));
        dispatch(showAll(false));
    }

    useEffect(() => {
        handleResetInitial();
        dispatch(tableColumns(ProductDetailsColumns));
    }, [dispatch, handleResetInitial])

    const handleChange = (name, value) => {
        const val = value.target.value || value.target.innerText;
        switch (name) {
            case 'AuthProductCode':
                const updateRegexWithNumber = validateRegexNumberWithStar(name, val, inputError);
                dispatch(updateInputError(updateRegexWithNumber));
                break;
            case 'GPI14':
                const updateRegexInclNumbers = validateRegexOnlyNumbers(name, val, inputError);
                dispatch(updateInputError(updateRegexInclNumbers));
                break;
            case 'NDC':
                const updateRegexOnlyChar = validateRegexNumbersSpecialCharLong(name, val, inputError);
                dispatch(updateInputError(updateRegexOnlyChar));
                break;
            default:
                break;
        }

        const updatedData = {
            ...data,
            [name]: val
        };
        dispatch(currentData(updatedData));
    }

    const handleSearch = () => {
        dispatch(showAll(false));
        dispatch(searchClicked(true));
        dispatch(searchTriggered(!searchTriggeredValue));
    }

    const handleShowAll = () => {
        dispatch(showAll(true));
        dispatch(clearData());
        dispatch(searchClicked(false));
        dispatch(searchTriggered(!searchTriggeredValue));
    }

    return (
        <>
            {loading && <Spinner />}
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="authProductCode"
                        label="AuthProductCode"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('AuthProductCode', e)}
                        value={data.AuthProductCode}
                        error={inputError.AuthProductCode}
                        helperText={inputError.AuthProductCode ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="gPI14"
                        label="GPI14"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('GPI14', e)}
                        value={data.GPI14}
                        error={inputError.GPI14}
                        helperText={inputError.GPI14 ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="nDC"
                        label="NDC"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('NDC', e)}
                        value={data.NDC}
                        error={inputError.NDC}
                        helperText={inputError.NDC ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
            </Grid>
            <Grid container spacing={3} sx={{ justifyContent: 'center', paddingTop: '24px' }}>
                <Button
                    variant="contained"
                    onClick={handleShowAll}
                >
                    Show All
                </Button>
                <Button
                    variant={(!isSearched && !showAllValue) ? "contained" : "outlined"}
                    style={isSearched ? { color: 'var(--primary-font-color)' } : undefined}
                    size="small"
                    onClick={handleReset}
                    endIcon={<Refresh />}
                    disabled={!isSearched && !showAllValue}
                >
                    Reset
                </Button>
                <Button
                    variant={(!isSearchEnable || hasAnyError) ? "contained" : "contained"}
                    size="large"
                    onClick={handleSearch}
                    endIcon={<Search />}
                    style={(isSearchEnable && !hasAnyError) ? { color: 'var(--primary-font-color)', background: 'var(--primary-color)', border: '1px solid var(--primary-font-color)' } : undefined}
                    disabled={!isSearchEnable || hasAnyError}
                >
                    Search
                </Button>
            </Grid>
            {((isSearched) || (showAllValue)) && <Table />}
        </>
    )
}

export default ProductDetail;